import React, { useState, useEffect, useMemo } from "react";
import { Container, Box, Fade, Snackbar, Alert, Typography, Button, Paper } from "@mui/material";
import {
  Groups as GroupsIcon,
  AssignmentInd as AssignmentIndIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import HeaderSection from "./DateUsers/HeaderSection";
import UsersManagement from "./UsersManagement";
import UserHistory from "./DateUsers/UserHistory";
import Register from "./DateUsers/Register";
import UsersService from "../../services/UsersService";

const CoursesAssign = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("students");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await UsersService.getUsers();
      
      if (!Array.isArray(data)) {
        throw new Error('Los datos recibidos no tienen el formato esperado');
      }
      
      const processed = data.map((item) => ({
        ...item,
        id: item.id || Math.random().toString(36).substring(2, 9),
        name: item.name || item.fullName || 'Sin nombre',
        email: item.email || item.emailAddress || 'Sin email',
        status: item.status || 'activo',
        profile: item.profile || item.role || 'Sin perfil',
      }));
      
      setUsers(processed);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Error al cargar los usuarios";
      setError(errorMessage);
      console.error("Error fetching users:", err);
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus =
        statusFilter === "all" || 
        (user.status && user.status.toLowerCase() === statusFilter.toLowerCase());
      
      const matchesProfile =
        profileFilter === "all" || 
        (user.profile && user.profile === profileFilter);

      return matchesSearch && matchesStatus && matchesProfile;
    });
  }, [users, searchTerm, statusFilter, profileFilter]);

  const uniqueProfiles = useMemo(() => {
    const profiles = users
      .map((user) => user.profile)
      .filter((profile) => profile && profile !== "undefined" && profile !== "null");
    return [...new Set(profiles)];
  }, [users]);

  const handleOpenHistory = (user) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`¿Seguro que deseas eliminar a ${user.name}?`)) {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
        setSnackbar({
          open: true,
          message: `${user.name} ha sido eliminado exitosamente`,
          severity: "success",
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Error al eliminar el usuario",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    await fetchUsers();
    setSnackbar({
      open: true,
      message: "Datos actualizados correctamente",
      severity: "success",
    });
  };

  const handleExport = () => {
    setSnackbar({
      open: true,
      message: "Exportación iniciada",
      severity: "info",
    });
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);       
    setActiveTab("register");    
  };

  const handleHistoryUser = (user) => {
    navigate(`/user-history/${user.id}`);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setProfileFilter("all");
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Mostrar error si no se pueden cargar los usuarios
  if (error && users.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <HeaderSection />
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            border: '1px solid #fecaca',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              boxShadow: '0 8px 25px rgba(239, 68, 68, 0.3)',
            }}
          >
            <GroupsIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          
          <Typography variant="h5" color="error" gutterBottom sx={{ fontWeight: 700 }}>
            Error al cargar los usuarios
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
            {error}
          </Typography>
          
          <Button
            variant="contained"
            size="large"
            onClick={fetchUsers}
            startIcon={<RefreshIcon />}
            sx={{
              backgroundColor: '#ef4444',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                backgroundColor: '#dc2626',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(239, 68, 68, 0.4)',
              },
              transition: 'all 0.3s ease-in-out',
              fontWeight: 600,
            }}
          >
            Reintentar
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <HeaderSection />

        {activeTab === "students" && (
          <UsersManagement
            filteredUsers={filteredUsers}
            isLoading={isLoading}
            setActiveTab={setActiveTab}
            handleExport={handleExport}
            handleRefresh={handleRefresh}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            profileFilter={profileFilter}
            setProfileFilter={setProfileFilter}
            clearFilters={clearFilters}
            uniqueProfiles={uniqueProfiles}
            handleOpenHistory={handleOpenHistory}
            handleDeleteUser={handleDeleteUser}
            handleEditUser={handleEditUser}
          />
        )}

        {activeTab === "register" && (
          <Fade in timeout={700}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              <Register selectedUser={selectedUser} />
            </Paper>
          </Fade>
        )}

        <UserHistory
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          user={selectedUser}
        />

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ 
              width: '100%',
              borderRadius: 3,
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CoursesAssign;
